// @ts-nocheck
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Mail, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { api } from '@/lib/api'

interface TokenInfoResponse {
  success: boolean
  data?: {
    email?: string
    verificationToken?: string
  }
  error?: string
}

export function CheckEmailPage() {
  const [isResending, setIsResending] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [token, setToken] = useState('')

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const tokenParam = searchParams.get('token')
    if (!tokenParam) {
      navigate('/signup?error=invalid_session', { replace: true })
      return
    }

    setToken(tokenParam)
    fetchEmailFromToken(tokenParam)
  }, [navigate, searchParams])

  const logo = useMemo(() => '/logo.png', [])

  const fetchEmailFromToken = async (tokenParam: string) => {
    try {
      const result = await api.post<TokenInfoResponse>('/auth/token-info', { token: tokenParam })
      if (result.success && result.data?.email) {
        setEmail(result.data.email)
      } else {
        setError('Invalid verification session. Please sign up again.')
      }
    } catch (err) {
      console.error('Failed to load verification details', err)
      setError('Failed to load verification details. Please sign up again.')
    }
  }

  const handleResendVerification = async () => {
    if (!email) {
      setError('Email address not found. Please sign up again.')
      return
    }

    setIsResending(true)
    setError(null)
    setResendMessage(null)

    try {
      const result = await api.post<TokenInfoResponse>('/auth/resend-verification', { email })

      if (result.success) {
        if (result.data?.verificationToken) {
          const newToken = result.data.verificationToken
          window.history.replaceState({}, '', `/check-email?token=${newToken}`)
          setToken(newToken)
        }
        setResendMessage('Verification email has been resent. Please check your inbox.')
      } else {
        setError(result.error || 'Failed to resend verification email')
      }
    } catch (err) {
      console.error('Failed to resend verification email', err)
      setError('Something went wrong. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="bg-card border border-border shadow-clean rounded-3xl overflow-hidden w-full max-w-2xl p-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <img src={logo} alt="Gathio Logo" className="w-8 h-8" />
            <span className="text-xl font-bold text-foreground">gathio</span>
          </div>
        </div>

        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-surface-variant rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-primary" />
          </div>

          <h2 className="text-4xl font-bold text-foreground mb-3 tracking-tight">
            Check Your Email
          </h2>

          <p className="text-xl text-muted-foreground leading-relaxed mb-6">
            We've sent a verification link to your email address.
            Click the link in the email to verify your account and get started.
          </p>

          <div className="bg-muted/50 border border-border rounded-xl p-4 mb-6">
            <p className="text-sm text-muted-foreground mb-1">Verification email sent to:</p>
            <p className="font-medium text-foreground">{email || 'Loading...'}</p>
          </div>
        </div>

        {resendMessage && (
          <Alert className="mb-6 border-green-500/50 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-700">
              {resendMessage}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="mb-6 border-destructive/50 bg-destructive/10">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <AlertDescription className="text-destructive">
              {error}
            </AlertDescription>
          </Alert>
        )}

        <div className="text-center mb-8">
          <p className="text-muted-foreground mb-4">
            Didn't receive the email? Check your spam folder or request a new one.
          </p>

          <Button
            onClick={handleResendVerification}
            disabled={isResending || !email}
            variant="outline"
            className="h-12 px-6 border border-border hover:border-primary/50 bg-background hover:bg-accent text-foreground font-medium rounded-xl transition-smooth shadow-sm hover:shadow-md"
          >
            {isResending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Resend Email
              </>
            )}
          </Button>
        </div>

        <div className="bg-muted/30 border border-border rounded-xl p-6 mb-8">
          <h3 className="font-semibold text-foreground mb-3">What's next?</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              Check your email inbox (and spam folder)
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              Click the verification link in the email
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
              You'll be automatically signed in and redirected to set up your organization
            </li>
          </ul>
        </div>

        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            Want to use a different email?{' '}
            <Link to="/signup" className="text-primary hover:underline font-medium">
              Sign up again
            </Link>{' '}
            or{' '}
            <Link to="/login" className="text-primary hover:underline font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default CheckEmailPage
